import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { tokenStore } from "@/lib/tokenStore";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { UserPlus, Mail, KeyRound, User as UserIcon, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${token}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setInvitation(data);
        setForm((f) => ({ ...f, name: data.parent_name || "" }));
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(formatApiError(err, "Invitation invalide"));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Mot de passe trop court (6 caractères min.)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: invitation.email,
        password: form.password,
        name: form.name,
        invitation_token: token,
      });
      tokenStore.set(data.token);
      toast.success("Compte créé · Bienvenue à Lafayette");
      navigate("/dashboard");
      window.location.reload();
    } catch (err) {
      toast.error(formatApiError(err, "Création impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  if (user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <ShieldCheck className="mx-auto text-emerald-600" size={32}/>
        <h1 className="font-display text-2xl font-bold mt-3">Vous êtes déjà connecté</h1>
        <p className="text-sm text-slate-600 mt-2">Cette invitation s'utilise depuis un compte non connecté.</p>
        <Link to="/dashboard" className="btn-primary mt-5">Aller à mon espace</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center overline text-slate-500">Vérification de l'invitation…</div>;
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <AlertTriangle className="mx-auto text-red-600" size={32}/>
        <h1 className="font-display text-2xl font-bold mt-3">Invitation invalide</h1>
        <p className="text-sm text-slate-600 mt-2">{error}</p>
        <p className="text-xs text-slate-500 mt-4">
          Si vous pensez qu'il s'agit d'une erreur, contactez l'administration de Lafayette International School à <a className="underline" href="mailto:collegelyceelafayette@gmail.com">collegelyceelafayette@gmail.com</a> ou au +33 4 93 62 00 29.
        </p>
        <Link to="/" className="btn-ghost mt-6">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-72px)] grid md:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <span className="overline">Invitation Lafayette</span>
          <h1 className="font-display text-4xl font-bold mt-2">Activez votre espace famille.</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Bienvenue {invitation.parent_name || ""}. Suite à votre rendez-vous avec l'administration, voici votre accès personnel à MyLafayette Admissions.
          </p>

          {invitation.student_name && (
            <div className="mt-5 card p-3 flex items-center gap-3 bg-amber-50 border-amber-200">
              <UserIcon size={16} className="text-amber-700"/>
              <div className="text-sm">
                <div className="overline text-amber-700">Dossier ouvert pour</div>
                <div className="font-semibold">{invitation.student_name}</div>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4 mt-6">
            <div>
              <label className="field-label">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="invite-email" type="email" value={invitation.email} readOnly className="field-input pl-9 bg-slate-50 cursor-not-allowed"/>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Email lié à votre invitation — non modifiable.</p>
            </div>
            <div>
              <label className="field-label">Nom complet du référent</label>
              <div className="relative">
                <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-input pl-9" placeholder="Jeanne Dupont"/>
              </div>
            </div>
            <div>
              <label className="field-label">Mot de passe</label>
              <div className="relative">
                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="invite-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field-input pl-9" placeholder="6 caractères minimum"/>
              </div>
            </div>
            <button data-testid="invite-submit" type="submit" disabled={submitting} className="btn-primary w-full justify-center">
              {submitting ? <Loader2 size={14} className="animate-spin"/> : <UserPlus size={14}/>}
              {submitting ? "Activation…" : "Activer mon espace"}
            </button>
          </form>

          <p className="text-[11px] text-slate-400 mt-6">
            Vous recevez ce lien parce qu'un rendez-vous d'admission a eu lieu avec l'établissement. Il n'est pas possible de créer un compte sans cette invitation.
          </p>
        </div>
      </div>
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1400&q=80"
          alt="Bibliothèque Lafayette"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(10,27,46,0.85) 0%, rgba(10,27,46,0.35) 50%, rgba(10,27,46,0.6) 100%)" }}/>
        <div className="absolute bottom-12 left-10 right-10 text-white">
          <span className="overline" style={{ color: "var(--c-accent)" }}>Lafayette International School</span>
          <h2 className="font-display text-4xl font-bold mt-2 leading-tight">Bienvenue dans <span className="font-serif-italic">votre nouveau chapitre.</span></h2>
        </div>
      </div>
    </div>
  );
}
